'use client';

import { useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import Tooltip from '@mui/material/Tooltip';
import { alpha, useTheme } from '@mui/material/styles';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { Iconify } from 'src/components/iconify';
import { useAppContext } from 'src/context/app';
import { toast } from 'src/components/snackbar';
import axiosInstance, { endpoints } from 'src/lib/axios';
import { hittaDemoOrganisation } from '../demo-organisationer';

export function DemoCompanionBar() {
  const theme = useTheme();
  const router = useRouter();
  const { organisation, uppdateraValdOrganisation } = useAppContext();
  const [collapsed, setCollapsed] = useState(false);
  const [aterstaller, setAterstaller] = useState(false);

  // Uppslag enbart på organisationsnummer. Namnmatchning ("Valnöten", "Demo") slog till på
  // riktiga föreningar som råkade heta så och visade guidefältet för dem.
  const demo = hittaDemoOrganisation(organisation?.organisationsNummer);

  const hanteraAterstall = async () => {
    if (!demo) return;
    try {
      setAterstaller(true);
      const res = await axiosInstance.post(endpoints.demo.återställ(demo.profil));
      if (res?.data) {
        uppdateraValdOrganisation(res.data);
      }
      toast.success('Demodata har återställts till standardläge!');
      router.refresh();
    } catch (err: any) {
      toast.error(`Kunde inte återställa demodata: ${err?.message || 'Ett fel uppstod'}`);
    } finally {
      setAterstaller(false);
    }
  };

  if (!demo) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        width: '100%',
        px: { xs: 2, sm: 3 },
        py: 1,
        bgcolor: alpha(theme.palette.background.default, 0.95),
        backdropFilter: 'blur(8px)',
        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
      }}
    >
      <Card
        sx={{
          p: 1.5,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          borderColor: alpha(theme.palette.primary.main, 0.24),
          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Chip
              icon={<Iconify icon="solar:stars-minimalistic-bold" width={16} />}
              label={`Demoläge: ${organisation?.organisationsNamn ?? demo.subtitle}`}
              color="primary"
              variant="soft"
              sx={{ fontWeight: 'fontWeightBold' }}
            />
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: { xs: 'none', md: 'inline' } }}
            >
              Utforska applikationens funktioner via snabbvalen nedan
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              disabled={aterstaller}
              onClick={hanteraAterstall}
              startIcon={
                <Iconify icon={aterstaller ? 'solar:restart-circle-bold' : 'solar:restart-bold'} />
              }
            >
              {aterstaller ? 'Återställer...' : 'Återställ demodata'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => router.push(paths.dashboard.demo)}
              startIcon={<Iconify icon="solar:widget-add-bold" />}
            >
              Välj annan organisation
            </Button>
            <Tooltip title={collapsed ? 'Visa guidefält' : 'Minimera guidefält'}>
              <IconButton size="small" onClick={() => setCollapsed(!collapsed)}>
                <Iconify
                  icon={collapsed ? 'solar:alt-arrow-down-bold' : 'solar:alt-arrow-up-bold'}
                />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Collapse in={!collapsed}>
          <Box
            sx={{
              mt: 1.5,
              pt: 1,
              borderTop: `1px dashed ${alpha(theme.palette.primary.main, 0.15)}`,
            }}
          >
            <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
              {demo.steg.map((steg) => (
                <Chip
                  key={steg.id}
                  icon={<Iconify icon={steg.ikon} width={16} />}
                  label={steg.titel}
                  clickable
                  onClick={() => router.push(steg.path)}
                  variant="outlined"
                  color={steg.id === 'lån' ? 'warning' : 'primary'}
                  sx={{
                    flexShrink: 0,
                    fontWeight: 'fontWeightMedium',
                    bgcolor: 'background.paper',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                    },
                  }}
                />
              ))}
            </Stack>
          </Box>
        </Collapse>
      </Card>
    </Box>
  );
}
